export const LANGUAGES = ["RO", "RU"] as const;
export type Language = (typeof LANGUAGES)[number];

export const DIRECTIONS = ["RO_TO_RU", "RU_TO_RO"] as const;
export type Direction = (typeof DIRECTIONS)[number];

export const PARTS_OF_SPEECH = [
  "NOUN",
  "VERB",
  "ADJ",
  "ADV",
  "PRON",
  "PREP",
  "CONJ",
  "INTERJ",
  "NUMERAL",
  "ARTICLE",
  "PHRASE",
] as const;
export type PartOfSpeech = (typeof PARTS_OF_SPEECH)[number];

// Parts of speech we generate Pexels images for in study cards. Concrete
// nouns, verbs, adjectives, and adverbs return useful image hits;
// pronouns, prepositions, conjunctions, articles, etc. return random
// noise that hurts more than it helps. Phrases/numerals are excluded for
// now — the gloss is too unpredictable to give a confident image.
//
// Used by the queue DTO builder: a card is given an `imageQuery` only if
// its (effective) word's POS is in this list AND it has an
// `englishGloss`. Out-of-list words still render — text-only.
export const IMAGE_POS_ALLOWLIST = ["NOUN", "VERB", "ADJ", "ADV"] as const satisfies readonly PartOfSpeech[];
export type ImageAllowedPos = (typeof IMAGE_POS_ALLOWLIST)[number];

export const GENDERS = ["M", "F", "N"] as const;
export type Gender = (typeof GENDERS)[number];

export const REVIEW_TARGETS = ["WORD", "GRAMMAR", "QUIZ"] as const;
export type ReviewTargetType = (typeof REVIEW_TARGETS)[number];

// Quiz item kinds. Mirrors the Prisma QuizKind enum — string values
// must match exactly. CLOZE is the default (single sentence with
// blank); TRANSFORM adds a source sentence shown above the target.
// Both render as MCQ (low reps) or type-in (matured) — the kind only
// changes UI chrome, not the SRS pipeline.
export const QUIZ_KINDS = ["CLOZE", "TRANSFORM"] as const;
export type QuizKind = (typeof QUIZ_KINDS)[number];

// CEFR-style proficiency levels. A1–C2 are standard CEFR; A0 is informal
// "pre-A1" used here as a starter tier (the first ~50 words, designed to
// give a fast first level-up that anchors engagement). Used as tags on
// both words (`defaultTags: ["A1"]` in import files) and grammar rules.
//
// These are *orthogonal* to topical tags — `A1` alone on both sides is
// meaningless for grammar↔word matching since every A1 item would match
// every other A1 item. Excluded from tag-based grammar/word matching;
// surfaced as a distinct concept for UI rendering (a dedicated level
// badge, not a `#A1` hashtag) and for Level-mode queue scoping.
//
// Order matters — UI uses array index to compare ("level X comes before Y").
export const CEFR_LEVELS = ["A0", "A1", "A2", "B1", "B2", "C1", "C2"] as const;
export type CefrLevel = (typeof CEFR_LEVELS)[number];

const CEFR_SET = new Set<string>(CEFR_LEVELS);

/** Is this tag a CEFR level? Case-sensitive — we want exact matches only. */
export function isCefrLevel(tag: string): tag is CefrLevel {
  return CEFR_SET.has(tag);
}

/**
 * Returns the subset of tags that are *not* CEFR levels — the ones that
 * should participate in tag-based matching. Preserves input order so
 * display order is stable if callers also use this for rendering.
 */
export function nonCefrTags(tags: readonly string[]): string[] {
  return tags.filter((t) => !CEFR_SET.has(t));
}

/**
 * Inverse — returns the CEFR tags from a set. Convenient for UI code that
 * wants to render levels separately from topical tags.
 */
export function cefrTagsOnly(tags: readonly string[]): CefrLevel[] {
  return tags.filter(isCefrLevel);
}

/**
 * How the study queue is scoped for a user. Mirrors the Prisma `StudyMode`
 * enum — string values must match exactly.
 *   OPEN      — current behavior, all levels mixed.
 *   LEVEL     — guided path: new cards limited to `currentLevel`,
 *               reviews include levels ≤ `currentLevel`.
 *   SCENARIOS — capability-driven path: home is the journal of
 *               capabilities, current activity is one scenario at a
 *               time. Phase 3 of `scenarios.md`.
 */
export const STUDY_MODES = ["OPEN", "LEVEL", "SCENARIOS"] as const;
export type StudyMode = (typeof STUDY_MODES)[number];

// ─── Per-level progress (GET /progress/levels) ─────────────────────
//
// Shape returned by the per-level stats endpoint. Mirrored on both
// sides because both the API service and the web hook need to know
// the field set, and CefrLevel/StudyMode already live here.

/**
 * Where this level sits in the user's journey. Computed server-side so
 * the UI never has to recreate the rules.
 *   active — `level === user.currentLevel` (regardless of progress).
 *   done   — past levels (index < currentLevel) that have any data. The
 *            "are they really done" question is answered by `readyForExam`
 *            on the active level, not by re-checking past levels.
 *   next   — the level immediately after currentLevel in CEFR order,
 *            even if it has zero data yet (so the UI can render a
 *            preview row).
 *   locked — everything else: future levels beyond `next`, and past
 *            levels that have no data (e.g. user starts at A1, A0
 *            stays empty).
 */
export const LEVEL_STATUSES = ["done", "active", "next", "locked"] as const;
export type LevelStatus = (typeof LEVEL_STATUSES)[number];

export type LevelProgress = {
  level: CefrLevel;
  vocab: { known: number; total: number; pct: number };
  grammar: { known: number; total: number; pct: number };
  status: LevelStatus;
  // True when this is the user's `currentLevel` AND both vocab/grammar
  // pcts have hit the 85% threshold. UI flips the Home CTA from
  // "Continue {level}" → "Final exam · {nextLevel} readiness" on this.
  readyForExam: boolean;
};

export type LevelsResponse = {
  currentLevel: CefrLevel;
  studyMode: StudyMode;
  // Always 7 entries (every level in CEFR_LEVELS), in canonical order.
  // Empty levels appear with zeros + status "locked" so the UI can
  // render the full ladder without conditional row logic.
  levels: LevelProgress[];
};

// Threshold (in percent) above which a level is considered "complete"
// for the readiness gate. Both vocab AND grammar must clear it before
// `readyForExam` flips true. Lives here so UI copy and server logic
// reference the same number ("Reach 85% on both…").
export const LEVEL_COMPLETION_THRESHOLD_PCT = 85;

// Threshold (in percent) above which a Phase 7 level-up exam attempt
// counts as a pass. **Intentionally lower** than the readiness gate
// (85%) — exam questions are harder than reviews, so the bar to clear
// the exam is set so a learner who's reliably reaching 85% on reviews
// can pass with margin rather than failing on a single bad section.
//
// Per `levels-update.md` Phase 7 design notes.
export const EXAM_PASS_PCT = 80;

// Total questions per attempt + per-section breakdown. The split is
// locked in `levels-update.md` Phase 7 (15 + 10 + 10 + 10 + 5 = 50).
// Listed as named constants because the section count drives both
// sampling logic (server) and progress-dot rendering (web), and a
// magic-number split would silently bend if the spec ever changes.
export const EXAM_TOTAL_QUESTIONS = 50;
export const EXAM_SECTION_COUNTS = {
  vocab_mcq: 15,
  vocab_type: 10,
  grammar_mcq: 10,
  grammar_fill: 10,
  grammar_transform: 5,
} as const;
export type ExamSection = keyof typeof EXAM_SECTION_COUNTS;
export const EXAM_SECTIONS: ExamSection[] = [
  "vocab_mcq",
  "vocab_type",
  "grammar_mcq",
  "grammar_fill",
  "grammar_transform",
];

// Rejection if a failed attempt within this window exists. "Single
// retry window keeps the design honest — if you fail, you actually go
// back to studying" (per the Phase 7 plan). Hours, not days, so the
// constant reads naturally where it's used.
export const EXAM_COOLDOWN_HOURS = 24;

// Soft timer — client renders a countdown that shifts color (yellow at
// 2 min remaining, red at 30 s). The server doesn't enforce expiry:
// per the Phase 7 design's open question, the v1 lean is "soft warning,
// not hard cap" — submitting late still grades, the visual just nags.
//
// `startedAt` is server-recorded on attempt creation, so even after
// the 15-min window the client can still post answers; cooldown
// machinery handles the case where someone bombs the exam by sitting
// on it past 24 h (a fresh attempt can be started after that).
export const EXAM_DURATION_MINUTES = 15;
